import { useEffect, useRef } from 'react'

/** How long to wait before each new try at the profile: quickly at first, then every two minutes. */
export const PROFILE_RETRY_DELAYS = [5_000, 15_000, 30_000, 60_000, 120_000]

/**
 * Keep asking for the signed-in person's profile while `active`, backing off between tries
 * and trying at once when the tab comes back into view or the connection returns.
 *
 * The profile carries the person's roles. One failed load used to leave them on a role-less
 * stand-in for the whole visit, with nothing trying again: an HRBP sat through two Annual
 * I&G Review sessions unable to move anyone or add an action (Victor, 5 Oct 2026).
 */
export function useProfileRetry(active: boolean, retry: () => void, delays: number[] = PROFILE_RETRY_DELAYS) {
  const attempt = useRef(0)
  const latest = useRef(retry)
  latest.current = retry

  useEffect(() => {
    if (!active) {
      attempt.current = 0
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = () => {
      const wait = delays[Math.min(attempt.current, delays.length - 1)]
      timer = setTimeout(() => {
        attempt.current += 1
        latest.current()
        schedule()
      }, wait)
    }
    schedule()

    const now = () => latest.current()
    const onVisible = () => {
      if (document.visibilityState === 'visible') latest.current()
    }
    window.addEventListener('online', now)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('online', now)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [active, delays])
}
